/**
 * Чистая логика подстановки карточки товара платформы в поля калькулятора.
 *
 * Отдельный модуль, а не экспорт из `Calculator.tsx`, по той же причине, что и
 * `calculatorDraft.ts`: экспорт не-компонента из файла с компонентом ломает
 * `react-refresh/only-export-components`. Здесь дополнительный мотив — тестируемость:
 * рендер калькулятора (antd Card/Table/Modal, useAppColors, react-i18next) слишком
 * тяжёл для юнит-теста правила «пришло поле — ставим, не пришло — не трогаем»,
 * а чистая функция без setState проверяется напрямую.
 */

import type { ProductCardFields } from '../../shared/api/client';
import type { CalculatorDraftDims } from './calculatorDraft';

/** Числовые поля состояния данных калькулятора, которые карточка товара может подставить. */
export interface ProductCardFieldUpdate {
  purchasePrice?: number;
  shippingCostPerKg?: number;
  unitWeightKg?: number;
  sellingPrice?: number;
  referralFee?: number;
  fbaFee?: number;
  batchQuantity?: number;
}

export interface ProductCardApplyResult {
  fields: ProductCardFieldUpdate;
  /** Все три — l/w/h — приходят разом или не приходят вовсе (см. computeProductCardUpdate). */
  dims?: CalculatorDraftDims & { l: number; w: number; h: number };
  asin?: string;
  purchasePriceCny?: number;
  /** Курс, который использовался при расчёте `priceInYuan` карточки — становится новым текущим. */
  cnyRate?: number;
}

/**
 * Чистое отображение карточки товара платформы в поля калькулятора.
 *
 * Габариты — уже дюймы, вес — уже килограммы: конвертация не нужна (единицы карточки
 * совпадают с единицами калькулятора, см. JSDoc `fetchProductCard` в `shared/api/client.ts`).
 *
 * `currentCnyRate` нужен только для одного случая: карточка даёт `purchasePrice`
 * в долларах без пары `priceInYuan`/`yuanToDollarRate` — тогда поле CNY
 * пересчитывается из USD текущим курсом, как это уже делает эффект восстановления
 * `initialData` в `Calculator.tsx` для сохранённых расчётов.
 */
export function computeProductCardUpdate(
  card: ProductCardFields,
  currentCnyRate: number | null,
): ProductCardApplyResult {
  const fields: ProductCardFieldUpdate = {};
  if (card.sellingPrice != null) fields.sellingPrice = card.sellingPrice;
  if (card.referralFee != null) fields.referralFee = card.referralFee;
  if (card.fbaFee != null) fields.fbaFee = card.fbaFee;
  if (card.unitWeightKg != null) fields.unitWeightKg = card.unitWeightKg;
  if (card.batchQuantity != null) fields.batchQuantity = card.batchQuantity;
  if (card.shippingCostPerKg != null) fields.shippingCostPerKg = card.shippingCostPerKg;

  let purchasePriceCny: number | undefined;
  let cnyRate: number | undefined;

  if (card.priceInYuan != null && card.yuanToDollarRate != null && card.yuanToDollarRate > 0) {
    // Карточка прислала пару юань+курс — она авторитетнее текущего live-курса:
    // именно по этому курсу и был посчитан `priceInYuan` на стороне платформы.
    purchasePriceCny = card.priceInYuan;
    cnyRate = card.yuanToDollarRate;
    fields.purchasePrice = +(card.priceInYuan / card.yuanToDollarRate).toFixed(2);
  } else if (card.purchasePrice != null) {
    fields.purchasePrice = card.purchasePrice;
    if (currentCnyRate !== null && currentCnyRate > 0) {
      purchasePriceCny = +(card.purchasePrice * currentCnyRate).toFixed(2);
    }
  }

  const result: ProductCardApplyResult = { fields };
  if (card.height != null && card.width != null && card.length != null) {
    result.dims = { l: card.length, w: card.width, h: card.height };
  }
  if (card.asin) result.asin = card.asin;
  if (purchasePriceCny != null) result.purchasePriceCny = purchasePriceCny;
  if (cnyRate != null) result.cnyRate = cnyRate;
  return result;
}

/** Есть ли в результате хоть одно поле, которое стоит подставлять. */
export function productCardUpdateHasChanges(update: ProductCardApplyResult): boolean {
  return Object.keys(update.fields).length > 0 || update.dims != null || update.asin != null;
}
